// Centralized SEO data source.
// The real site origin is resolved from the VITE_SITE_URL environment variable,
// with a fallback to the canonical domain so the site never breaks when the
// variable is not injected locally.

export const SITE_URL =
  (import.meta.env.VITE_SITE_URL as string) ||
  "https://allchinabuyspread.monster";

export const SITE_NAME = "AllChinaBuy Spreadsheet Hub";

// The only valid image URL provided by the authoritative SEO data (favicon).
export const SITE_IMAGE =
  "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/49750b7389edabeda6497a215e0702e5.png";

export const LANG_CODES = ["en", "fr", "da", "de"];

export interface PageSeo {
  title: string;
  description: string;
  keywords: string;
  path: string;
}

export const homeSeo: PageSeo = {
  title: "AllChinaBuy Spreadsheet 2026 — Products, Finds & Buying Guides",
  description:
    "Explore the latest AllChinaBuy spreadsheet resources, product finds, seller information, QC guides, shipping tips and practical buying advice — all organized in one place.",
  keywords:
    "AllChinaBuy spreadsheet, AllChinaBuy products, AllChinaBuy finds, AllChinaBuy buying guide, AllChinaBuy review, AllChinaBuy shipping",
  path: "/",
};

export const articleSeo: Record<string, Omit<PageSeo, "path">> = {
  "allchinabuy-spreadsheet": {
    title: "AllChinaBuy Spreadsheet 2026: Products, Finds & Buying Guide",
    description:
      "The complete guide to understanding AllChinaBuy spreadsheets, product categories, and how to find the best deals in 2026.",
    keywords:
      "AllChinaBuy spreadsheet, AllChinaBuy products list, AllChinaBuy finds, AllChinaBuy buying guide",
  },
  "allchinabuy-review": {
    title: "AllChinaBuy Review 2026: Features, Pros, Cons & Buying Guide",
    description:
      "A comprehensive review of the AllChinaBuy platform, covering features, warehouse services, shipping options, and honest pros and cons for international buyers.",
    keywords:
      "AllChinaBuy review, AllChinaBuy legit, AllChinaBuy pros and cons, AllChinaBuy buying guide",
  },
  "allchinabuy-shipping": {
    title: "AllChinaBuy Shipping Cost 2026: Complete Guide & Comparison",
    description:
      "Everything you need to know about AllChinaBuy shipping costs, delivery times, shipping lines, and how to calculate volumetric weight for international orders.",
    keywords:
      "AllChinaBuy shipping, AllChinaBuy shipping cost, AllChinaBuy delivery time, AllChinaBuy shipping line",
  },
  "allchinabuy-legit": {
    title: "Is AllChinaBuy Legit? 2026 Safety & Trust Guide",
    description:
      "An honest safety guide covering seller trust, payment security, counterfeit risks, and red flags to watch for when buying through AllChinaBuy.",
    keywords:
      "AllChinaBuy legit, AllChinaBuy safety, AllChinaBuy trust, AllChinaBuy review",
  },
  "allchinabuy-coupon": {
    title: "AllChinaBuy Coupon 2026: How Discounts Work",
    description:
      "A complete guide to AllChinaBuy coupons, discount codes, eligibility requirements, and how to maximize savings on your orders.",
    keywords:
      "AllChinaBuy coupon, AllChinaBuy discount, AllChinaBuy promo code, AllChinaBuy savings",
  },
  "allchinabuy-warehouse": {
    title: "AllChinaBuy Warehouse Guide: Storage, QC & Shipping",
    description:
      "Learn how AllChinaBuy warehouse works, including storage time, QC inspection, package consolidation, and shipping preparation processes.",
    keywords:
      "AllChinaBuy warehouse, AllChinaBuy storage, AllChinaBuy QC, AllChinaBuy shipping",
  },
  "allchinabuy-buying-guide": {
    title: "AllChinaBuy Buying Guide 2026: How to Shop Smart",
    description:
      "A step-by-step buying guide covering product selection, seller research, order placement, and smart strategies to avoid common purchasing mistakes.",
    keywords:
      "AllChinaBuy buying guide, AllChinaBuy how to shop, AllChinaBuy tips, AllChinaBuy products",
  },
  "allchinabuy-qc-guide": {
    title: "AllChinaBuy QC Guide: How to Read QC Photos",
    description:
      "Learn how to interpret QC photos, spot defects, and decide whether to approve or reject items before they ship from the warehouse.",
    keywords:
      "AllChinaBuy QC, AllChinaBuy quality control, AllChinaBuy QC photos, AllChinaBuy warehouse",
  },
  "allchinabuy-shipping-cost": {
    title: "AllChinaBuy Shipping Cost 2026: Complete Guide",
    description:
      "Understand the factors that determine shipping costs, including weight, destination, service type, and how volumetric weight affects your total.",
    keywords:
      "AllChinaBuy shipping cost, AllChinaBuy volumetric weight, AllChinaBuy shipping line, AllChinaBuy delivery",
  },
  "allchinabuy-discount-code": {
    title: "AllChinaBuy Discount Code 2026: Offers & Savings",
    description:
      "How AllChinaBuy discount codes work, where to find current offers, eligibility rules, and tips to apply savings to your orders.",
    keywords:
      "AllChinaBuy discount code, AllChinaBuy promo, AllChinaBuy savings, AllChinaBuy coupon",
  },
  "allchinabuy-seller-guide": {
    title: "AllChinaBuy Seller Guide: How to Evaluate Sellers",
    description:
      "A practical guide to evaluating seller trustworthiness, checking reviews, understanding ratings, and reducing risk when choosing vendors.",
    keywords:
      "AllChinaBuy seller, AllChinaBuy trusted sellers, AllChinaBuy seller review, AllChinaBuy products",
  },
  "allchinabuy-best-finds": {
    title: "Best AllChinaBuy Finds 2026: Popular Products",
    description:
      "A curated look at the most popular AllChinaBuy finds across categories, what makes them worth buying, and how to spot quality items.",
    keywords:
      "AllChinaBuy finds, AllChinaBuy best finds, AllChinaBuy popular products, AllChinaBuy spreadsheet",
  },
  "allchinabuy-safety-guide": {
    title: "AllChinaBuy Safety Guide: Buyer Protection Tips",
    description:
      "Essential safety tips for international buyers, covering payment protection, privacy, scam prevention, and a practical buyer checklist.",
    keywords:
      "AllChinaBuy safety, AllChinaBuy buyer protection, AllChinaBuy scam prevention, AllChinaBuy legit",
  },
  "allchinabuy-beginner-guide": {
    title: "AllChinaBuy Beginner Guide: Getting Started",
    description:
      "Everything a first-time buyer needs to know, from creating an account to placing your first order and understanding the basics.",
    keywords:
      "AllChinaBuy beginner guide, AllChinaBuy getting started, AllChinaBuy tutorial, AllChinaBuy buying guide",
  },
  "allchinabuy-product-research": {
    title: "AllChinaBuy Product Research Guide: Find Deals",
    description:
      "Learn how to research products effectively, compare prices across sellers, and identify genuine quality before committing to a purchase.",
    keywords:
      "AllChinaBuy product research, AllChinaBuy price comparison, AllChinaBuy find deals, AllChinaBuy products",
  },
  "allchinabuy-parcel-consolidation": {
    title: "AllChinaBuy Parcel Consolidation: Save on Shipping",
    description:
      "How parcel consolidation works, when to combine packages, and how bundling items can reduce your overall shipping costs.",
    keywords:
      "AllChinaBuy parcel consolidation, AllChinaBuy combine packages, AllChinaBuy save shipping, AllChinaBuy warehouse",
  },
};